import React, { useEffect } from "react";

import { DialogContent } from "@material-ui/core";
import { FormikErrors, FormikTouched, getIn, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { Row } from "components/Layout";
import TabRow, { DefaultTab } from "components/TabSelector/TabRow";

import {
  getActiveLivestockAgentDeployment,
  getBusinesses,
  getSaleSubTypeById,
} from "selectors";

import { withNamespace } from "components/Form/FormikControls/lib";

import { MobileTransactionalCreateSaleFormValues } from "./types";
import {
  MobileTransactionalCreateSaleModalTabs,
  MobileTransactionalCreateSaleModalTabId,
  MobileTransactionalCreateSaleModalTabOrder,
} from "./constants";
import { FormStatus } from "constants/form";
import MobileTransactionSaleTabSelector from "./TabSelector";
import { getTabFields, getTabStatusColor } from "./tabManager";
import theme from "constants/theme";
import { TabLabel } from "components/TabSelector";
import {
  buyerSplitNamespace,
  consignmentNamespace,
  saleLotNamespace,
  saleNamespace,
  skinLotsNamespace,
  vendorSplitNamespace,
} from ".";
import { PriceTab, ReviewTab, WhatTab, WhenTab, WhoTab } from "./Tabs";

const buildSaleTitle = (vendorName, buyerName, saleType, saleSubType) => {
  const parties =
    vendorName && buyerName
      ? `${vendorName} - ${buyerName}`
      : vendorName || buyerName;

  // some sale sub types share the same name as the sale - don't double it up
  const saleSubTypeName =
    !!saleSubType?.name && saleSubType?.name !== saleType
      ? saleSubType?.name
      : null;

  return [parties, saleType, saleSubTypeName].filter(Boolean).join(" ");
};

function getTabStatus(
  tabId: MobileTransactionalCreateSaleModalTabId,
  errors: FormikErrors<MobileTransactionalCreateSaleFormValues>,
  touched: FormikTouched<MobileTransactionalCreateSaleFormValues>,
): string | undefined {
  const fields = getTabFields(
    saleLotNamespace,
    consignmentNamespace,
    saleNamespace,
  )[tabId];

  if (fields.length === 0) {
    return undefined;
  }

  const anyFieldHasError = fields.some(f => getIn(errors, f));
  const noFieldsTouched = !fields.some(f => getIn(touched, f));

  if (anyFieldHasError) {
    return FormStatus.ERROR;
  }

  if (noFieldsTouched) {
    return FormStatus.UNTOUCHED;
  }
  return FormStatus.TOUCHED;
}

interface FormProps {
  activeTab: MobileTransactionalCreateSaleModalTabId;
  setActiveTab: (id: MobileTransactionalCreateSaleModalTabId) => void;
}

export const Form = ({ activeTab, setActiveTab }: FormProps) => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<MobileTransactionalCreateSaleFormValues>();
  const {
    sale: {
      sale_type: saleType,
      sale_sub_type_id: saleSubTypeId,
      species_id: speciesId,
    },
    consignment: { vendor_id: vendorId },
    saleLot: { buyer_id: buyerId },
  } = values;

  const saleTitleTouched = touched.sale?.sale_title;

  const businessByIdLookup = useSelector(getBusinesses);

  const vendorName = businessByIdLookup[vendorId]?.name || null;
  const buyerName = businessByIdLookup[buyerId]?.name || null;
  const currentSaleSubType = useSelector(getSaleSubTypeById(saleSubTypeId));

  const baseSaleTitle = buildSaleTitle(
    vendorName,
    buyerName,
    saleType,
    currentSaleSubType,
  );

  useEffect(() => {
    if (!saleTitleTouched) {
      setFieldValue(withNamespace(saleNamespace, "sale_title"), baseSaleTitle);
    }
  }, [baseSaleTitle, saleTitleTouched, setFieldValue]);

  const activeLivestockAgentDeployment = useSelector(
    getActiveLivestockAgentDeployment,
  );

  const tabs = MobileTransactionalCreateSaleModalTabOrder.map(id => {
    const tabStatus = getTabStatus(id, errors, touched);
    return {
      id,
      title: id,
      component: DefaultTab,
      TabSelectorComponent: MobileTransactionSaleTabSelector,
      TabLabelComponent: TabLabel,
      tabLabelComponentProps: { color: getTabStatusColor(tabStatus) },
    };
  });

  return (
    <>
      <Row
        fullWidth
        flex
        justifyCenter
        overflow="scroll"
        flexShrink={0}
        style={{ borderBottom: "1px" }}
      >
        <TabRow
          tabs={tabs}
          selectedTab={activeTab}
          setSelectedTab={id =>
            setActiveTab(id as MobileTransactionalCreateSaleModalTabId)
          }
          backgroundColor="#fff"
          handleScrollIntoView
        />
      </Row>
      <DialogContent style={{ backgroundColor: theme.colors.grayF3 }}>
        {activeTab === MobileTransactionalCreateSaleModalTabs.WHO && (
          <WhoTab
            consignmentNamespace={consignmentNamespace}
            saleLotNamespace={saleLotNamespace}
            vendorSplitNamespace={vendorSplitNamespace}
            buyerSplitNamespace={buyerSplitNamespace}
          />
        )}
        {activeTab === MobileTransactionalCreateSaleModalTabs.WHAT && (
          <WhatTab
            saleNamespace={saleNamespace}
            consignmentNamespace={consignmentNamespace}
            saleLotNamespace={saleLotNamespace}
            skinsLotNamespace={skinLotsNamespace}
            deploymentId={activeLivestockAgentDeployment.id}
            speciesId={speciesId}
          />
        )}
        {activeTab === MobileTransactionalCreateSaleModalTabs.WHEN && (
          <WhenTab saleNamespace={saleNamespace} />
        )}
        {activeTab === MobileTransactionalCreateSaleModalTabs.PRICE && (
          <PriceTab saleLotNamespace={saleLotNamespace} />
        )}
        {activeTab === MobileTransactionalCreateSaleModalTabs.REVIEW && (
          <ReviewTab
            deploymentId={activeLivestockAgentDeployment.id}
            speciesId={speciesId}
            saleNamespace={saleNamespace}
          />
        )}
      </DialogContent>
    </>
  );
};
